import {
  Divider,
  Grid,
  StyledContainer,
  Text,
  Tooltip as UiTooltip,
  Icon,
  Stack,
} from '@piwikpro/ui-components';
import React from 'react';
import { ComponentDowntime } from '../model/ComponentDowntime';
import { UptimeGraphComponent } from './UptimeGraphComponent';
import { SeverityMapper, severityIcon } from '../helpers';
import { IncidentComment } from '../model';
import { useAppSelector } from '../hooks';
import { selectComponentsMapping } from '../incidentSlice';

type UptimeGraphListItemProps = {
  downtimes: ComponentDowntime[];
  component: string;
  start: Date;
  end: Date;
  selectedRangeLabel: string;
};

export const severity2msg: SeverityMapper = {
  ok: 'All Systems Operational',
  maintenance: 'Maintenance',
  info: 'Performance Degraded',
  disrupted: 'Performance Degraded',
  down: 'Downtime',
  unknown: 'Incident',
};

export function formatTime(cd: ComponentDowntime): string {
  const start = cd.downtime.start.toLocaleString();
  const end = cd.originalEnd.toLocaleString();
  if (
    cd.downtime.end.getTime() === 0 ||
    isNaN(cd.downtime.end.getTime()) ||
    cd.downtime.start > cd.originalEnd
  ) {
    return `${start}`;
  } else {
    return `${start} - ${end}`;
  }
}

export function formatTimeInComments(comment: IncidentComment): string {
  const timestamp = new Date(comment.timestamp).toLocaleString();
  return timestamp;
}

export function getDowntime(
  downtimes: ComponentDowntime[],
  component: string,
  start: Date,
  end: Date
) {
  const downtime = downtimes
    .filter((d) => d.name === component)
    .filter((d) => !['maintenance', 'info', 'ok'].includes(d.severity))
    .filter((d) => d.downtime.start <= end)
    .filter((d) => d.downtime.end >= start)
    // sort by start date ascending, otherwise only 1 incident might be calculated, others then will have start and end time set to acc.lastTimestamp
    .sort((a, b) => a.downtime.start.getTime() - b.downtime.start.getTime())
    .reduce(
      (acc, v) => {
        const start = Math.max(acc.lastTimestamp, v.downtime.start.getTime());
        const end = Math.min(
          Date.now(),
          Math.max(acc.lastTimestamp, v.downtime.end.getTime())
        );
        const duration = Math.max(0, end - start);
        acc.lastTimestamp = end;
        acc.downtimeMillis += duration;
        return acc;
      },
      {
        lastTimestamp: start.getTime(),
        downtimeMillis: 0,
      }
    );
  return downtime;
}

export function getUptimePct(
  downtime: { downtimeMillis: number },
  start: Date,
  end: Date
) {
  const rangeMillis = end.getTime() - start.getTime();
  const uptimePct =
    (100 * (rangeMillis - downtime.downtimeMillis)) / rangeMillis;

  return uptimePct;
}

const UptimeGraphListItem: React.FC<UptimeGraphListItemProps> = ({
  downtimes,
  component,
  start,
  end,
  selectedRangeLabel,
}) => {
  const componentsMapping = useAppSelector(selectComponentsMapping);
  const downtime = getDowntime(downtimes, component, start, end);
  const tz = new Date()
    .toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'short' })
    .substring(4);

  const uptimePct = getUptimePct(downtime, start, end);

  const ownDowntimes = downtimes.filter((d) => d.name === component);
  const ongoing = ownDowntimes.filter(
    (d) =>
      d.severity !== 'ok' &&
      d.downtime.start < end &&
      (d.downtime.end < d.downtime.start || d.downtime.end > end)
  );
  const description =
    componentsMapping.find((c) => c.displayName === component)?.description ??
    '';
  const hasTooltip = description.length > 0;
  const nameWithDescription = () => {
    if (hasTooltip) {
      return (
        <UiTooltip
          content={description}
          title=""
          inlineActivator={true}
          position={'bottom-start'}
        >
          <span>
            <Stack
              spacing="extraNarrow"
              horizontalAlignment="left"
              fullWidth={false}
            >
              <Text type={'strong'}>{component}</Text>
              <Icon name="info-circle" />
            </Stack>
          </span>
        </UiTooltip>
      );
    } else {
      return (
        <span>
          <Text type={'strong'}>{component}</Text>
        </span>
      );
    }
  };

  return (
    <Grid
      columnsTemplate="1fr"
      rowsTemplate="50fr"
      rowGap={'narrow'}
      key={component}
    >
      <Grid.Item>{nameWithDescription()}</Grid.Item>
      <Grid.Item horizontalAlignment="end">
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            whiteSpace: 'nowrap',
          }}
        >
          <Text type={'subdued'}> Uptime </Text>
          <Text type={'strong'}>{uptimePct.toFixed(2)} %</Text>
        </div>
      </Grid.Item>
      <Grid.Item
        key={component + '-chart'}
        columns={2}
        horizontalAlignment="center"
      >
        <UptimeGraphComponent
          name={component}
          start={start}
          end={end}
          width={1000}
          height={80}
          downtimes={downtimes}
        />
      </Grid.Item>
      <Grid.Item columns={2} horizontalAlignment="center">
        <Text type={'subdued'}>
          State ({selectedRangeLabel}, {tz})
        </Text>
      </Grid.Item>
      <>
        {ongoing.map((d) => {
          return (
            <Grid.Item columns={2}>
              <StyledContainer
                border={['bottom', 'left', 'right', 'top']}
                padding="wide"
                backgroundColor="gray"
              >
                <Grid columnsTemplate="1fr 1fr" rowGap={'narrow'}>
                  <Grid.Item>
                    <Text type="strong">
                      {severityIcon(d.severity)} {severity2msg[d.severity]}
                    </Text>
                  </Grid.Item>
                  <Grid.Item horizontalAlignment="end">
                    <Text type="subdued">{formatTime(d)}</Text>
                  </Grid.Item>
                  <Grid.Item columns={2}>
                    <Divider />
                  </Grid.Item>
                  {d.details.map((comment) => {
                    return (
                      <>
                        <Grid.Item horizontalAlignment="start">
                          <Text>{comment.text}</Text>
                        </Grid.Item>
                        <Grid.Item horizontalAlignment="end">
                          <Text type="subdued">
                            {formatTimeInComments(comment)}
                          </Text>
                        </Grid.Item>
                      </>
                    );
                  })}
                </Grid>
              </StyledContainer>
            </Grid.Item>
          );
        })}
      </>
    </Grid>
  );
};

export { UptimeGraphListItem };
